// Recycle Bin — shows soft-deleted domains and servers so an accidental delete
// can be undone. Each row can be Restored (back to its live list) or Deleted
// Permanently (irreversible). "Restore All" / "Empty Trash" act on a whole
// section. Data + mutations come from the shared React Query hooks; a restore or
// purge invalidates the matching ["domains"] / ["servers"] caches so every view
// refreshes automatically.
import { useContext } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import RestoreFromTrashOutlinedIcon from "@mui/icons-material/RestoreFromTrashOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import DashboardLayout from "../layouts/DashboardLayout";
import { COLORS } from "../theme/tokens";
import { HEADING_FONT } from "../theme/fonts";
import FlatCard from "../components/FlatCard";
import { useToast } from "../components/ToastProvider";
import { useConfirm } from "../components/ConfirmProvider";
import { AuthContext } from "../context/AuthContext";
import {
  useDomainsTrash,
  useRestoreDomains,
  usePurgeDomains,
  useServersTrash,
  useRestoreServers,
  usePurgeServers,
} from "../hooks/queries";

const colors = {
  text: COLORS.ink,
  textMuted: COLORS.muted,
  textFaint: COLORS.faint,
  cardBorder: COLORS.hairline,
  hover: COLORS.tile,
};

// MySQL DATETIME ("2026-07-09 12:34:56") / ISO → a short human string.
function formatWhen(value) {
  if (!value) return "—";
  const d = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// One trash table (Domains or Servers). Kept generic so both sections share the
// exact same layout and behaviour.
function TrashSection({
  title,
  icon,
  rows,
  canDelete,
  onRestore,
  onPurge,
  onRestoreAll,
  onEmpty,
  busy,
}) {
  return (
    <FlatCard sx={{ p: 0 }}>
      {/* Section header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2, borderBottom: `1px solid ${colors.cardBorder}` }}
        gap={2}
        flexWrap="wrap"
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {icon}
          <Typography sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: 17, color: colors.text }}>
            {title}
          </Typography>
          <Box
            sx={{
              minWidth: 26,
              height: 26,
              px: 0.6,
              borderRadius: "999px",
              background: COLORS.primarySoft,
              color: COLORS.primary,
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {rows.length}
          </Box>
        </Stack>

        {canDelete && (
          <Stack direction="row" spacing={2} sx={{ ml: "auto" }}>
            <Button
              onClick={onRestoreAll}
              disabled={busy || rows.length === 0}
              startIcon={<RestoreFromTrashOutlinedIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13.5,
                px: 1.8,
                py: 0.8,
                borderRadius: "10px",
                color: COLORS.primary,
                background: COLORS.primarySoft,
                "&:hover": { background: COLORS.primarySoft, filter: "brightness(0.97)" },
              }}
            >
              Restore All
            </Button>
            <Button
              onClick={onEmpty}
              disabled={busy || rows.length === 0}
              startIcon={<DeleteSweepOutlinedIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13.5,
                px: 1.8,
                py: 0.8,
                borderRadius: "10px",
                color: "#DC2626",
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.25)",
                "&:hover": { background: "rgba(239, 68, 68, 0.18)" },
              }}
            >
              Empty Trash
            </Button>
          </Stack>
        )}
      </Stack>

      {/* Body */}
      {rows.length === 0 ? (
        <Box sx={{ px: 3, py: 5, textAlign: "center", color: colors.textFaint }}>
          <Typography sx={{ fontSize: 14 }}>Nothing in trash.</Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: colors.textMuted, fontWeight: 700, fontSize: 12.5, borderColor: colors.cardBorder }}>
                  NAME
                </TableCell>
                <TableCell sx={{ color: colors.textMuted, fontWeight: 700, fontSize: 12.5, borderColor: colors.cardBorder }}>
                  DELETED
                </TableCell>
                <TableCell align="left" sx={{ color: colors.textMuted, fontWeight: 700, fontSize: 12.5, borderColor: colors.cardBorder }}>
                  ACTIONS
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ color: colors.text, fontWeight: 600, fontSize: 14, borderColor: colors.cardBorder }}>
                    {r.name || `#${r.id}`}
                  </TableCell>
                  <TableCell sx={{ color: colors.textMuted, fontSize: 13.5, borderColor: colors.cardBorder }}>
                    {formatWhen(r.deletedAt)}
                  </TableCell>
                  <TableCell align="right" sx={{ borderColor: colors.cardBorder }}>
                    {canDelete && (
                      <Stack direction="row" gap={0.5} justifyContent="flex-end">
                        <Tooltip title="Restore">
                          <IconButton
                            size="small"
                            onClick={() => onRestore(r)}
                            disabled={busy}
                            sx={{ color: COLORS.primary }}
                          >
                            <RestoreFromTrashOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete permanently">
                          <IconButton
                            size="small"
                            edge="end"
                            onClick={() => onPurge(r)}
                            disabled={busy}
                            sx={{ color: "#DC2626" }}
                          >
                            <DeleteForeverOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </FlatCard>
  );
}

function Trash() {
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useContext(AuthContext);
  const canDeleteDomains = hasPermission("domains", "delete");
  const canDeleteServers = hasPermission("servers", "delete");

  const { data: domainRows = [] } = useDomainsTrash();
  const { data: serverRows = [] } = useServersTrash();

  const restoreDomains = useRestoreDomains();
  const purgeDomains = usePurgeDomains();
  const restoreServers = useRestoreServers();
  const purgeServers = usePurgeServers();

  const domainsBusy = restoreDomains.isPending || purgeDomains.isPending;
  const serversBusy = restoreServers.isPending || purgeServers.isPending;

  // --- Domain actions ---
  async function restoreDomain(r) {
    try {
      await restoreDomains.mutateAsync([r.id]);
      toast.success(`Restored "${r.name}"`);
    } catch (err) {
      toast.error(err.message || "Failed to restore domain");
    }
  }
  async function purgeDomain(r) {
    const ok = await confirm({
      title: "Delete permanently?",
      message: `"${r.name}" will be removed forever. This cannot be undone.`,
      danger: true,
      confirmLabel: "Delete forever",
    });
    if (!ok) return;
    try {
      await purgeDomains.mutateAsync([r.id]);
      toast.success(`Deleted "${r.name}" permanently`);
    } catch (err) {
      toast.error(err.message || "Failed to delete domain");
    }
  }
  async function restoreAllDomains() {
    try {
      const res = await restoreDomains.mutateAsync({ all: true });
      toast.success(`Restored ${res?.restored ?? 0} domain(s)`);
    } catch (err) {
      toast.error(err.message || "Failed to restore domains");
    }
  }
  async function emptyDomains() {
    const ok = await confirm({
      title: "Empty domain trash?",
      message: "Every domain in the trash will be removed forever. This cannot be undone.",
      danger: true,
      confirmLabel: "Empty trash",
    });
    if (!ok) return;
    try {
      const res = await purgeDomains.mutateAsync({ all: true });
      toast.success(`Deleted ${res?.deleted ?? 0} domain(s) permanently`);
    } catch (err) {
      toast.error(err.message || "Failed to empty trash");
    }
  }

  // --- Server actions ---
  async function restoreServer(r) {
    try {
      await restoreServers.mutateAsync([r.id]);
      toast.success(`Restored "${r.name}"`);
    } catch (err) {
      toast.error(err.message || "Failed to restore server");
    }
  }
  async function purgeServer(r) {
    const ok = await confirm({
      title: "Delete permanently?",
      message: `"${r.name}" will be removed forever. This cannot be undone.`,
      danger: true,
      confirmLabel: "Delete forever",
    });
    if (!ok) return;
    try {
      await purgeServers.mutateAsync([r.id]);
      toast.success(`Deleted "${r.name}" permanently`);
    } catch (err) {
      toast.error(err.message || "Failed to delete server");
    }
  }
  async function restoreAllServers() {
    try {
      const res = await restoreServers.mutateAsync({ all: true });
      toast.success(`Restored ${res?.restored ?? 0} server(s)`);
    } catch (err) {
      toast.error(err.message || "Failed to restore servers");
    }
  }
  async function emptyServers() {
    const ok = await confirm({
      title: "Empty server trash?",
      message: "Every server in the trash will be removed forever. This cannot be undone.",
      danger: true,
      confirmLabel: "Empty trash",
    });
    if (!ok) return;
    try {
      const res = await purgeServers.mutateAsync({ all: true });
      toast.success(`Deleted ${res?.deleted ?? 0} server(s) permanently`);
    } catch (err) {
      toast.error(err.message || "Failed to empty trash");
    }
  }

  return (
    <DashboardLayout>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: HEADING_FONT, fontWeight: 800, fontSize: 26, color: colors.text }}>
          Recycle Bin
        </Typography>
        <Typography sx={{ color: colors.textMuted, fontSize: 14, mt: 0.5 }}>
          Deleted domains and servers are kept here. Restore them, or delete them permanently.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
          alignItems: "stretch",
        }}
      >
        <TrashSection
          title="Domains"
          icon={<DomainOutlinedIcon sx={{ fontSize: 22, color: COLORS.primary }} />}
          rows={domainRows}
          canDelete={canDeleteDomains}
          busy={domainsBusy}
          onRestore={restoreDomain}
          onPurge={purgeDomain}
          onRestoreAll={restoreAllDomains}
          onEmpty={emptyDomains}
        />

        <TrashSection
          title="Servers"
          icon={<DnsOutlinedIcon sx={{ fontSize: 22, color: COLORS.primary }} />}
          rows={serverRows}
          canDelete={canDeleteServers}
          busy={serversBusy}
          onRestore={restoreServer}
          onPurge={purgeServer}
          onRestoreAll={restoreAllServers}
          onEmpty={emptyServers}
        />
      </Box>
    </DashboardLayout>
  );
}

export default Trash;
